import { Component, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { TrendMetric } from '../../services/metric.service';
import { DateRange, Peer, StateService } from '../../services/state.service';
import { FilterTrendMetricPipe } from '../../pipes/filter-trend-metric.pipe';
import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';
import { OrderByPipe } from '../../../shared/pipes/order-by';
import { VisualService } from '../../services/visual.service';
import { SessionService } from '../../../shared/services/session.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

@RegisterComponent('TrendComponent')
@Component({
  selector: 'app-trend',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsDirective, FilterTrendMetricPipe, GroupByPipe, OrderByPipe, ChartInfoBoxComponent],
  templateUrl: './trend.component.html',
  styleUrl: './trend.component.scss',
  providers: [provideEcharts()]
})
export class TrendComponent extends VisualBaseComponent {
  readonly id = input.required<number>();
  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly peerName = inject(StateService).peerName;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  private sessionService = inject(SessionService);

  override initOptions = { height: 400 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    title: {
      show: false
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    axisLabel: {
      color: '#002f8e'
    },
    series: [
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => value != null ? VisualService.getValueDisplay(Number(value), this.metric()?.displayType) : '--'
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      }
    ]
  };

  trendMetrics = signal<TrendMetric[]>([]);
  metric = signal<TrendMetric | undefined>(undefined);
  noData = false;

  listOpen = false;
  openCategories: string[] = [];
  search = '';

  constructor() {
    super();

    effect(() => {
      const institution = this.institution();
      if (institution?.institutionType) {
        this.metricService.getTrendMetrics(institution.institutionType).subscribe(metrics => {
          setTimeout(() => {
            this.trendMetrics.set(metrics);
            const metricId = this.sessionService.get(`trend-metricId-${this.institution()?.institutionType}-${this.id()}`)?.toString() ?? this.metric()?.metricId ?? metrics[0]?.metricId;
            this.selectMetric(metricId);
          });
        });
      }
    });

    effect(() => {
      const peer = this.peer();
      const dateRange = this.dateRange();
      const metric = this.metric();
      const group = this.filteredGroup();
      if (this.chartInit() && peer && dateRange && metric && group) {
        this.updateVisual(peer, dateRange, metric);
      }
    });
  }

  public updateVisual(peer: Peer, dateRange: DateRange, metric: TrendMetric) {
    this.metricService.getMetricData(this.institution().iid, metric.metricId, peer)?.subscribe(data => {
      const dataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, data, dateRange);

      if (dataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      }
      else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }
        this.updateOptions = {
          xAxis: {
            data: dataSeries.dateSeries
          },
          series: [
            {
              name: this.institution()?.name,
              data: dataSeries.institutionSeries,
              color: this.colors[0],
              symbol: 'line',
              symbolSize: 0,
            },
            {
              name: this.peerName(),
              data: dataSeries.peerSeries,
              color: this.colors[1],
              symbol: 'line',
              symbolSize: 0,
            }
          ]
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }

  clearSearch() {
    this.search = '';
    this.openCategories = [];
  }

  isCategoryOpen(category: string) {
    return this.openCategories.find(c => c == category);
  }

  toggleCategory(category: string) {
    if (this.openCategories.find(c => c == category)) {
      this.openCategories = this.openCategories.filter(c => c != category);
    }
    else {
      this.openCategories.push(category);
    }
  }

  selectMetric(metricId: string): void {
    const metric = this.trendMetrics().find(m => m.metricId == metricId);
    if (metric) {
      this.sessionService.set(`trend-metricId-${this.institution()?.institutionType}-${this.id()}`, metricId, true);
      this.metric.set(metric);
      this.listOpen = false;
    }
  }
}
